import React from 'react';
import { render, cleanup, act, fireEvent } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderComponent(element, assertions, waitMs = 200) {
  const logCapture = createLogCapture();
  let snapshot = null;
  try {
    let result;
    await act(async () => { result = render(element); });
    await act(async () => { await new Promise(r => setTimeout(r, waitMs)); });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

// ─── Shared fixtures ─────────────────────────────────────────────────────────

const MOCK_PROJECT = { uuid: 'p-test-1', name: 'Test Project Alpha' };
const MOCK_PROJECTS = [MOCK_PROJECT];

const QUEUED_FRAGMENT             = { uuid: 'frag-queued-1',   status: 'queued',              result: null, speaker_names: null };
const PENDING_WEBHOOK_FRAGMENT    = { uuid: 'frag-pending-1',  status: 'pending_webhook',     result: null, speaker_names: null };
const FAILED_FRAGMENT             = { uuid: 'frag-failed-1',   status: 'failed',              result: null, speaker_names: null };
const SERVICE_UNAVAILABLE_FRAGMENT = { uuid: 'frag-svc-1',    status: 'service_unavailable', result: null, speaker_names: null };

export const analysisFlowTests = [

  // ─── UploadFileWithTypes ──────────────────────────────────────────────────────

  {
    id: 'upload-type-selector-renders',
    name: 'UploadFileWithTypes: renders all 4 type buttons',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      return await renderComponent(
        withWrapper(
          <UploadFileWithTypes handleClose={() => {}} handleNewFileUpload={() => {}} />,
          '/analysis'
        ),
        async () => {
          assert(document.body.querySelector('[data-testid="upload-type-audio"]') !== null,         'upload-type-audio button must render');
          assert(document.body.querySelector('[data-testid="upload-type-transcription"]') !== null, 'upload-type-transcription button must render');
          assert(document.body.querySelector('[data-testid="upload-type-pdf"]') !== null,           'upload-type-pdf button must render');
          assert(document.body.querySelector('[data-testid="upload-type-video"]') !== null,         'upload-type-video button must render');
        }
      );
    },
  },

  {
    id: 'upload-type-selector-audio',
    name: 'UploadFileWithTypes: clicking Audio calls handleNewFileUpload("Audio")',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      let calledWith = null;
      return await renderComponent(
        withWrapper(
          <UploadFileWithTypes handleClose={() => {}} handleNewFileUpload={(type) => { calledWith = type; }} />,
          '/analysis'
        ),
        async () => {
          const btn = document.body.querySelector('[data-testid="upload-type-audio"]');
          assert(btn !== null, 'upload-type-audio must be present');
          await act(async () => { fireEvent.click(btn); });
          assert(calledWith === 'Audio', `handleNewFileUpload called with "${calledWith}", expected "Audio"`);
        }
      );
    },
  },

  {
    id: 'upload-type-selector-transcription',
    name: 'UploadFileWithTypes: clicking Transcription calls handleNewFileUpload("Transcription")',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      let calledWith = null;
      return await renderComponent(
        withWrapper(
          <UploadFileWithTypes handleClose={() => {}} handleNewFileUpload={(type) => { calledWith = type; }} />,
          '/analysis'
        ),
        async () => {
          const btn = document.body.querySelector('[data-testid="upload-type-transcription"]');
          assert(btn !== null, 'upload-type-transcription must be present');
          await act(async () => { fireEvent.click(btn); });
          assert(calledWith === 'Transcription', `handleNewFileUpload called with "${calledWith}", expected "Transcription"`);
        }
      );
    },
  },

  {
    id: 'upload-type-selector-pdf',
    name: 'UploadFileWithTypes: clicking PDF calls handleNewFileUpload("Pdf")',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      let calledWith = null;
      return await renderComponent(
        withWrapper(
          <UploadFileWithTypes handleClose={() => {}} handleNewFileUpload={(type) => { calledWith = type; }} />,
          '/analysis'
        ),
        async () => {
          const btn = document.body.querySelector('[data-testid="upload-type-pdf"]');
          assert(btn !== null, 'upload-type-pdf must be present');
          await act(async () => { fireEvent.click(btn); });
          assert(calledWith === 'Pdf', `handleNewFileUpload called with "${calledWith}", expected "Pdf"`);
        }
      );
    },
  },

  {
    id: 'upload-type-selector-video',
    name: 'UploadFileWithTypes: clicking Video calls handleNewFileUpload("Video")',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      let calledWith = null;
      return await renderComponent(
        withWrapper(
          <UploadFileWithTypes handleClose={() => {}} handleNewFileUpload={(type) => { calledWith = type; }} />,
          '/analysis'
        ),
        async () => {
          const btn = document.body.querySelector('[data-testid="upload-type-video"]');
          assert(btn !== null, 'upload-type-video must be present');
          await act(async () => { fireEvent.click(btn); });
          assert(calledWith === 'Video', `handleNewFileUpload called with "${calledWith}", expected "Video"`);
        }
      );
    },
  },

  {
    id: 'upload-type-selector-cancel',
    name: 'UploadFileWithTypes: cancel button calls handleClose',
    run: async () => {
      const UploadFileWithTypes = require('../../Components/NewAnalysisElements/UploadFileWithTypes').default;
      let closed = false;
      return await renderComponent(
        withWrapper(
          <UploadFileWithTypes handleClose={() => { closed = true; }} handleNewFileUpload={() => {}} />,
          '/analysis'
        ),
        async () => {
          const btn = document.body.querySelector('[data-testid="upload-type-cancel"]');
          assert(btn !== null, 'upload-type-cancel must be present');
          await act(async () => { fireEvent.click(btn); });
          assert(closed, 'handleClose must be called when cancel is clicked');
        }
      );
    },
  },

  // ─── NewAudioFileUploadModal Step 1 ──────────────────────────────────────────

  {
    id: 'audio-upload-modal-step1-fields',
    name: 'NewAudioFileUploadModal: Step 1 renders all form fields',
    run: async () => {
      const NewAudioFileUploadModal = require('../../Components/Files/NewAudioFileUploadModal').default;
      return await renderComponent(
        withWrapper(
          <NewAudioFileUploadModal handleClose={() => {}} project={MOCK_PROJECT} />,
          '/analysis'
        ),
        async () => {
          assert(document.body.querySelector('[data-testid="audio-upload-meeting-name"]') !== null, 'audio-upload-meeting-name must render');
          assert(document.body.querySelector('[data-testid="audio-upload-language"]') !== null,      'audio-upload-language must render');
          assert(document.body.querySelector('[data-testid="audio-upload-speakers"]') !== null,      'audio-upload-speakers must render');
          assert(document.body.querySelector('[data-testid="audio-upload-next"]') !== null,          'audio-upload-next must render');
        }
      );
    },
  },

  // ─── UploadVideoFileModal Step 1 ─────────────────────────────────────────────

  {
    id: 'video-upload-modal-step1-fields',
    name: 'UploadVideoFileModal: Step 1 renders all form fields',
    run: async () => {
      const UploadVideoFileModal = require('../../Components/Files/UploadVideoFileModal').default;
      return await renderComponent(
        withWrapper(
          <UploadVideoFileModal handleClose={() => {}} project={MOCK_PROJECT} />,
          '/analysis'
        ),
        async () => {
          assert(document.body.querySelector('#file_name') !== null,           'file_name input must render in video upload step 1');
          assert(document.body.querySelector('#language') !== null,            'language select must render in video upload step 1');
          assert(document.body.querySelector('#number_of_speakers') !== null,  'number_of_speakers input must render in video upload step 1');
        }
      );
    },
  },

  // ─── TextUploadModal (PDF) Step 1 ────────────────────────────────────────────

  {
    id: 'pdf-upload-modal-step1-fields',
    name: 'TextUploadModal (PDF): Step 1 renders form fields',
    run: async () => {
      const TextUploadModal = require('../../Components/Text/TextUploadModal').default;
      return await renderComponent(
        withWrapper(
          <TextUploadModal handleClose={() => {}} projects={MOCK_PROJECTS} />,
          '/analysis'
        ),
        async () => {
          assert(document.body.querySelector('[data-testid="pdf-upload-meeting-name"]') !== null, 'pdf-upload-meeting-name must render');
          assert(document.body.querySelector('[data-testid="pdf-upload-language"]') !== null,      'pdf-upload-language must render');
          assert(document.body.querySelector('[data-testid="pdf-upload-next"]') !== null,          'pdf-upload-next must render');
        }
      );
    },
  },

  // ─── TextTranscriptionModal Step 1 ───────────────────────────────────────────

  {
    id: 'transcription-upload-modal-step1-renders',
    name: 'TextTranscriptionUploadModal: Step 1 renders dialog with name field',
    run: async () => {
      const TextTranscriptionUploadModal = require('../../Components/Text/TextTranscriptionModal').default;
      return await renderComponent(
        withWrapper(
          <TextTranscriptionUploadModal handleClose={() => {}} projects={MOCK_PROJECTS} />,
          '/analysis'
        ),
        async () => {
          const dialog = document.body.querySelector('[role="dialog"]');
          assert(dialog !== null, 'transcription upload dialog must render');
          assert(document.body.querySelector('input[name="name"]') !== null, 'name input must render in transcription step 1');
        }
      );
    },
  },

  // ─── AudioEmotionAnalysisCard: additional statuses ───────────────────────────

  {
    id: 'audio-emotion-card-queued',
    name: 'AudioEmotionAnalysisCard: renders card for queued status',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={QUEUED_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card !== null, 'emotion-analysis-card must render for queued fragment');
        }
      );
    },
  },

  {
    id: 'audio-emotion-card-pending-webhook',
    name: 'AudioEmotionAnalysisCard: renders card for pending_webhook status',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={PENDING_WEBHOOK_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card !== null, 'emotion-analysis-card must render for pending_webhook fragment');
        }
      );
    },
  },

  {
    id: 'audio-emotion-card-failed',
    name: 'AudioEmotionAnalysisCard: renders nothing for failed status',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={FAILED_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card === null, 'emotion-analysis-card must NOT render for failed fragment');
        }
      );
    },
  },

  {
    id: 'audio-emotion-card-service-unavailable',
    name: 'AudioEmotionAnalysisCard: renders nothing for service_unavailable status',
    run: async () => {
      const AudioEmotionAnalysisCard = require('../../Components/NewAnalysisElements/AudioEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <AudioEmotionAnalysisCard
            fragment={SERVICE_UNAVAILABLE_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="emotion-analysis-card"]');
          assert(card === null, 'emotion-analysis-card must NOT render for service_unavailable fragment');
        }
      );
    },
  },

  // ─── VideoEmotionAnalysisCard: additional statuses ───────────────────────────

  {
    id: 'video-emotion-card-queued',
    name: 'VideoEmotionAnalysisCard: renders card for queued status',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={QUEUED_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card !== null, 'video-emotion-analysis-card must render for queued fragment');
        }
      );
    },
  },

  {
    id: 'video-emotion-card-pending-webhook',
    name: 'VideoEmotionAnalysisCard: renders card for pending_webhook status',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={PENDING_WEBHOOK_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card !== null, 'video-emotion-analysis-card must render for pending_webhook fragment');
        }
      );
    },
  },

  {
    id: 'video-emotion-card-failed',
    name: 'VideoEmotionAnalysisCard: renders nothing for failed status',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={FAILED_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card === null, 'video-emotion-analysis-card must NOT render for failed fragment');
        }
      );
    },
  },

  // ─── Upload modal multi-step flows ───────────────────────────────────────────

  {
    id: 'audio-upload-step1-next-advances-to-step2',
    name: 'NewAudioFileUploadModal: clicking Next on Step 1 advances to Step 2 (dropzone)',
    run: async () => {
      const NewAudioFileUploadModal = require('../../Components/Files/NewAudioFileUploadModal').default;
      return await renderComponent(
        withWrapper(
          <NewAudioFileUploadModal handleClose={() => {}} project={MOCK_PROJECT} />,
          '/analysis'
        ),
        async () => {
          const nextBtn = document.body.querySelector('[data-testid="audio-upload-next"]');
          assert(nextBtn !== null, 'audio-upload-next button must exist in step 1');
          await act(async () => { fireEvent.click(nextBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[data-testid="audio-upload-dropzone"]') !== null, 'audio-upload-dropzone must appear in step 2');
        }
      );
    },
  },

  {
    id: 'pdf-upload-step1-next-advances-to-step2',
    name: 'TextUploadModal (PDF): clicking Next on Step 1 advances to Step 2 (dropzone)',
    run: async () => {
      const TextUploadModal = require('../../Components/Text/TextUploadModal').default;
      return await renderComponent(
        withWrapper(
          <TextUploadModal handleClose={() => {}} projects={MOCK_PROJECTS} />,
          '/analysis'
        ),
        async () => {
          const nextBtn = document.body.querySelector('[data-testid="pdf-upload-next"]');
          assert(nextBtn !== null, 'pdf-upload-next button must exist in step 1');
          await act(async () => { fireEvent.click(nextBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('[data-testid="pdf-upload-dropzone"]') !== null, 'pdf-upload-dropzone must appear in step 2');
        }
      );
    },
  },

  {
    id: 'video-upload-step1-next-advances-to-step2',
    name: 'UploadVideoFileModal: clicking Next on Step 1 advances to Step 2 (file picker)',
    run: async () => {
      const UploadVideoFileModal = require('../../Components/Files/UploadVideoFileModal').default;
      return await renderComponent(
        withWrapper(
          <UploadVideoFileModal handleClose={() => {}} project={MOCK_PROJECT} />,
          '/analysis'
        ),
        async () => {
          const dialog = document.body.querySelector('[role="dialog"]');
          assert(dialog !== null, 'Video upload dialog must render');
          const submitBtn = dialog.querySelector('button[type="submit"]');
          assert(submitBtn !== null, 'Step 1 submit (Next) button must exist');
          await act(async () => { fireEvent.click(submitBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          assert(document.body.querySelector('#file-upload-step2') !== null, '#file-upload-step2 file input must appear in step 2');
        }
      );
    },
  },

  {
    id: 'transcription-upload-step1-next-advances-to-step2',
    name: 'TextTranscriptionUploadModal: clicking Next on Step 1 advances to Step 2 (paste textarea)',
    run: async () => {
      const TextTranscriptionUploadModal = require('../../Components/Text/TextTranscriptionModal').default;
      return await renderComponent(
        withWrapper(
          <TextTranscriptionUploadModal handleClose={() => {}} projects={MOCK_PROJECTS} />,
          '/analysis'
        ),
        async () => {
          const nameInput = document.body.querySelector('input[name="name"]');
          assert(nameInput !== null, 'name input must exist in step 1');
          await act(async () => { fireEvent.change(nameInput, { target: { value: 'Test Transcription' } }); });
          const submitBtn = document.body.querySelector('[role="dialog"] button[type="submit"]');
          assert(submitBtn !== null, 'Step 1 submit button must exist');
          await act(async () => { fireEvent.click(submitBtn); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          const textareas = document.body.querySelectorAll('[role="dialog"] textarea');
          assert(textareas.length > 0, 'Paste textarea must appear in step 2');
        }
      );
    },
  },

  {
    id: 'transcription-upload-step2-next-advances-to-step3',
    name: 'TextTranscriptionUploadModal: pasting formatted text in Step 2 and clicking Next shows Step 3 review',
    run: async () => {
      const TextTranscriptionUploadModal = require('../../Components/Text/TextTranscriptionModal').default;
      return await renderComponent(
        withWrapper(
          <TextTranscriptionUploadModal handleClose={() => {}} projects={MOCK_PROJECTS} />,
          '/analysis'
        ),
        async () => {
          // Step 1 → submit
          const nameInput = document.body.querySelector('input[name="name"]');
          assert(nameInput !== null, 'name input must exist in step 1');
          await act(async () => { fireEvent.change(nameInput, { target: { value: 'Test' } }); });
          await act(async () => { fireEvent.click(document.body.querySelector('[role="dialog"] button[type="submit"]')); });
          await act(async () => { await new Promise(r => setTimeout(r, 150)); });
          // Step 2: paste transcription in the expected format
          const textarea = document.body.querySelector('[role="dialog"] textarea');
          assert(textarea !== null, 'Step 2 paste textarea must exist');
          const transcriptionText = 'Speaker A  0:00:00-0:00:05\nHello world.\n\nSpeaker B  0:00:06-0:00:10\nHi there.';
          await act(async () => { fireEvent.change(textarea, { target: { value: transcriptionText } }); });
          const step2Submit = document.body.querySelector('[role="dialog"] button[type="submit"]');
          assert(step2Submit !== null, 'Step 2 submit button must exist');
          await act(async () => { fireEvent.click(step2Submit); });
          await act(async () => { await new Promise(r => setTimeout(r, 200)); });
          // Step 3: read-only review textarea
          const reviewTextarea = document.body.querySelector('[role="dialog"] textarea[disabled], [role="dialog"] textarea[readonly]');
          assert(reviewTextarea !== null, 'Step 3 must show a disabled/readonly review textarea');
        },
        300
      );
    },
  },

  {
    id: 'video-emotion-card-service-unavailable',
    name: 'VideoEmotionAnalysisCard: renders nothing for service_unavailable status',
    run: async () => {
      const VideoEmotionAnalysisCard = require('../../Components/NewAnalysisElements/VideoEmotionAnalysisCard').default;
      return await renderComponent(
        withWrapper(
          <VideoEmotionAnalysisCard
            fragment={SERVICE_UNAVAILABLE_FRAGMENT}
            analysisParentUuid="parent-1"
            onStartAnalysis={() => {}}
            onCancelAnalysis={() => {}}
          />,
          '/analysis'
        ),
        async ({ container }) => {
          const card = container.querySelector('[data-testid="video-emotion-analysis-card"]');
          assert(card === null, 'video-emotion-analysis-card must NOT render for service_unavailable fragment');
        }
      );
    },
  },

];
